import AdvancedLyricsPanel from "@/components/audiolab/lyrics-video/AdvancedLyricsPanel";
import BackgroundPanel from "@/components/audiolab/lyrics-video/BackgroundPanel";
import EditorHeader from "@/components/audiolab/lyrics-video/EditorHeader";
import EditorPanel, {
  EDITOR_PANEL_MAX_WIDTH,
} from "@/components/audiolab/lyrics-video/EditorPanel";
import FirstRunBanner from "@/components/audiolab/lyrics-video/FirstRunBanner";
import LyricsPanel from "@/components/audiolab/lyrics-video/LyricsPanel";
import PlaybackBar from "@/components/audiolab/lyrics-video/PlaybackBar";
import PlayerCard from "@/components/audiolab/lyrics-video/PlayerCard";
import PresetPicker from "@/components/audiolab/lyrics-video/PresetPicker";
import { randomVisualizers } from "@/components/audiolab/lyrics-video/randomVisualizers";
import PanelSection from "@/components/audiolab/lyrics-video/ui/PanelSection";
import PanelTabs, {
  PanelTab,
} from "@/components/audiolab/lyrics-video/ui/PanelTabs";
import { panelTabId } from "@/components/audiolab/lyrics-video/ui/tabIds";
import SegmentedTabs, {
  SegmentedTabOption,
} from "@/components/audiolab/lyrics-video/ui/SegmentedTabs";
import { useEditorViewport } from "@/components/audiolab/lyrics-video/useEditorViewport";
import { useFirstRunBanner } from "@/components/audiolab/lyrics-video/useFirstRunBanner";
import { useLyricsVideoPanel } from "@/components/audiolab/lyrics-video/useEditorPanel";
import { useLyricsVideoExport } from "@/components/audiolab/lyrics-video/useLyricsVideoExport";
import { useLyricsVideoScene } from "@/components/audiolab/lyrics-video/useLyricsVideoScene";
import VisualizerPanel from "@/components/audiolab/lyrics-video/VisualizerPanel";
import UnlockAssetsModal from "@/components/audiolab/MainTrackPage/UnlockAssetsModal";
import { useLoggedInUser } from "@/components/providers/ApiProvider/hooks";
import {
  useTrackAssetsControllerGetTrackLyrics,
  useTrackAssetsControllerGetTrackWaveformData,
} from "@/lib/lutely-api/gen/track-assets/track-assets";
import { useTracksControllerTrackDetail } from "@/lib/lutely-api/gen/tracks/tracks";
import { getRandomVividHexColor } from "@/utils/random.utils";
import {
  applyPreset,
  ASPECT_RATIO_DIMENSIONS,
  AspectRatio,
  DEFAULT_SCENE,
  findMatchingPreset,
  LyricsVideoPreset,
  LyricsVideoScene,
  LyricsVideoStage,
  VisualizerConfig,
} from "@license-lounge/lyrics-video";
import {
  createFileRoute,
  useNavigate,
  useParams,
} from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FiMaximize, FiRotateCcw } from "react-icons/fi";

export const Route = createFileRoute("/audiolab/tracks/$TRACK_ID/lyrics-video")(
  {
    component: RouteComponent,
  },
);

/**
 * Every aspect ratio the scene supports. The mockup shows only the three social
 * formats, but 4:3 and 3:4 stay reachable so the tabs cannot silently strand a
 * saved scene on a ratio with no way back.
 */
const FORMAT_TABS: SegmentedTabOption<AspectRatio>[] = [
  { value: "16:9", label: "16:9", hint: "YouTube" },
  { value: "9:16", label: "9:16", hint: "Reels" },
  { value: "1:1", label: "1:1", hint: "Square" },
  { value: "4:3", label: "4:3", hint: "Classic" },
  { value: "3:4", label: "3:4", hint: "Portrait" },
];

type EditorPanelTab = "styles" | "text" | "background" | "animations";

/** Ties the header's toggle to the panel it controls, for `aria-controls`. */
const EDITOR_PANEL_ID = "lyrics-video-controls";

/** Prefix for the panel tabs' ids, so the tabpanel can name its own tab. */
const EDITOR_PANEL_TAB_PREFIX = "lyrics-video-tab";

/**
 * Styles / Text / Background / Animations, in that order.
 *
 * Animations is its own tab rather than a run of sections under Background.
 * Appended there, the visualizer controls sat below every background setting,
 * so reaching them meant scrolling past a tab's worth of unrelated controls —
 * and nothing in the tab strip said they existed at all.
 *
 * Label-only: four icons plus four labels do not fit 448px, and the labels are
 * the half that carries the meaning.
 */
const PANEL_TABS: PanelTab<EditorPanelTab>[] = [
  { value: "styles", label: "Styles" },
  { value: "text", label: "Text" },
  { value: "background", label: "Background" },
  { value: "animations", label: "Animations" },
];

/**
 * Lightness bands the two gradient stops are drawn from when a style is
 * shuffled. Separate bands, not one: two colours drawn from the same lightness
 * make a gradient you have to look for, and the point of the shuffle is a frame
 * that looks different at a glance.
 */
const SHUFFLE_GRADIENT_FROM = { minLightness: 45, maxLightness: 70 };
const SHUFFLE_GRADIENT_TO = { minLightness: 18, maxLightness: 55 };

function RouteComponent() {
  const [scene, setScene] = useState<LyricsVideoScene>(DEFAULT_SCENE);
  const [currentTime, setCurrentTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isUnlockOpen, setIsUnlockOpen] = useState(false);
  const audioRef = useRef<HTMLAudioElement>(null);

  // Single-step undo, so a shuffle and preset switches are reversible.
  const [previousScene, setPreviousScene] = useState<LyricsVideoScene | null>(
    null,
  );

  const [activeTab, setActiveTab] = useState<EditorPanelTab>("styles");

  const {
    isOpen: isPanelOpen,
    toggle: togglePanel,
    close: closePanel,
  } = useLyricsVideoPanel();

  const {
    ref: viewportRef,
    top: viewportTop,
    height: viewportHeight,
  } = useEditorViewport();

  const { TRACK_ID: trackId } = useParams({ strict: false });
  const navigate = useNavigate();
  const { data: user, hasActiveSubscription } = useLoggedInUser();
  const { isVisible: isBannerVisible, dismiss: dismissBanner } =
    useFirstRunBanner();

  const {
    data: trackDetail,
    isLoading: isTrackLoading,
    isError: isTrackError,
  } = useTracksControllerTrackDetail(trackId!, {
    query: { enabled: !!trackId && !!user },
  });

  // Keyed by track, not originalTrackAssetId: the detail endpoint withholds
  // that id from some owners, which left the editor with no waveform at all.
  const { data: waveFormData } = useTrackAssetsControllerGetTrackWaveformData(
    trackId ?? "",
    { query: { enabled: !!trackId && !!user } },
  );

  // Uncensored, as before the default read started masking every bad word:
  // the video is the artist's own, so only the always-masked words stay
  // hidden in the preview and the export.
  const { data: lyricsData } = useTrackAssetsControllerGetTrackLyrics(
    { trackId: trackDetail?.trackId!, unmasked: true },
    { query: { enabled: !!trackDetail?.trackId } },
  );

  const applyScene = useCallback((restored: LyricsVideoScene) => {
    setScene(restored);
  }, []);

  const {
    status: saveStatus,
    lastSavedAt,
    save: saveScene,
    flush: flushScene,
    hasSavedScene,
  } = useLyricsVideoScene({
    trackId: trackDetail?.trackId,
    scene,
    onSceneRestored: applyScene,
  });

  const updateLyrics = useCallback(
    (patch: Partial<LyricsVideoScene["lyrics"]>) =>
      setScene((current) => ({
        ...current,
        lyrics: { ...current.lyrics, ...patch },
      })),
    [],
  );

  const updateShadow = useCallback(
    (patch: Partial<LyricsVideoScene["lyrics"]["shadow"]>) =>
      setScene((current) => ({
        ...current,
        lyrics: {
          ...current.lyrics,
          shadow: { ...current.lyrics.shadow, ...patch },
        },
      })),
    [],
  );

  const updateBackground = useCallback(
    (patch: Partial<LyricsVideoScene["background"]>) =>
      setScene((current) => ({
        ...current,
        background: { ...current.background, ...patch },
      })),
    [],
  );

  const changeFormat = useCallback((aspectRatio: AspectRatio) => {
    // Resizes the frame only — lyric timing state is untouched by a format switch.
    setScene((current) => ({
      ...current,
      canvas: { aspectRatio, ...ASPECT_RATIO_DIMENSIONS[aspectRatio] },
    }));
  }, []);

  const selectPreset = useCallback((preset: LyricsVideoPreset) => {
    setScene((current) => {
      setPreviousScene(current);
      return applyPreset(current, preset);
    });
  }, []);

  const randomizeStyle = useCallback(() => {
    setScene((current) => {
      setPreviousScene(current);

      // Background and visualizers only — type is deliberately left alone.
      // A shuffle that also re-rolled the font size, colour and rotation of the
      // lyrics undid typography you had just set by hand, which made the button
      // something you stopped pressing once your text was how you wanted it.
      // Restricted to the frame behind the words, it stays useful all the way
      // through the edit.
      return {
        ...current,
        background: {
          ...current.background,
          // Always a gradient: a shuffled solid varies along one axis, so
          // consecutive presses read as "it changed the colour" rather than as
          // a new look.
          type: "gradient",
          gradientColor1: getRandomVividHexColor(SHUFFLE_GRADIENT_FROM),
          gradientColor2: getRandomVividHexColor(SHUFFLE_GRADIENT_TO),
          gradientType: Math.random() < 0.5 ? "linear" : "radial",
          gradientAngle: Math.round(Math.random() * 360),
        },
        visualizers: randomVisualizers(),
      };
    });
  }, []);

  const undo = useCallback(() => {
    if (!previousScene) {
      return;
    }
    setScene(previousScene);
    setPreviousScene(null);
  }, [previousScene]);

  const addVisualizer = useCallback(() => {
    setScene((current) => ({
      ...current,
      visualizers: [
        ...current.visualizers,
        {
          id: `${Date.now()}${Math.random().toString(36).slice(2, 7)}`,
          type: "bars",
          // Inherits the active style's color, per spec.
          color: current.lyrics.color,
          opacity: 0.8,
          posX: 0,
          posY: 0,
          rotateX: 0,
          rotateY: 0,
          rotateZ: 0,
        },
      ],
    }));
  }, []);

  const updateVisualizer = useCallback(
    (id: string, patch: Partial<VisualizerConfig>) =>
      setScene((current) => ({
        ...current,
        visualizers: current.visualizers.map((visualizer) =>
          visualizer.id === id ? { ...visualizer, ...patch } : visualizer,
        ),
      })),
    [],
  );

  const removeVisualizer = useCallback(
    (id: string) =>
      setScene((current) => ({
        ...current,
        visualizers: current.visualizers.filter(
          (visualizer) => visualizer.id !== id,
        ),
      })),
    [],
  );

  // Audio element wiring: the custom PlaybackBar drives it, so the native
  // controls stay hidden.
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) {
      return;
    }

    audio.src = trackDetail?.fileUrl || "";
    setIsPlaying(false);
    setCurrentTime(0);

    let animationFrameId: number;

    const tick = () => {
      setCurrentTime(audio.currentTime);
      animationFrameId = requestAnimationFrame(tick);
    };

    const handlePlay = () => {
      setIsPlaying(true);
      tick();
    };

    const handlePause = () => {
      setIsPlaying(false);
      cancelAnimationFrame(animationFrameId);
    };

    audio.addEventListener("play", handlePlay);
    audio.addEventListener("pause", handlePause);
    audio.addEventListener("ended", handlePause);

    return () => {
      cancelAnimationFrame(animationFrameId);
      audio.removeEventListener("play", handlePlay);
      audio.removeEventListener("pause", handlePause);
      audio.removeEventListener("ended", handlePause);
    };
  }, [trackDetail?.fileUrl]);

  const togglePlay = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) {
      return;
    }
    if (audio.paused) {
      void audio.play();
    } else {
      audio.pause();
    }
  }, []);

  const seek = useCallback((time: number) => {
    const audio = audioRef.current;
    if (audio) {
      audio.currentTime = time;
    }
    setCurrentTime(time);
  }, []);

  const activePreset = useMemo(() => findMatchingPreset(scene), [scene]);
  const duration = waveFormData?.duration ?? 0;

  // A plan or a $5 unlock of this one track — the same two paths as every other
  // paid asset. `unlockedAt` is set by the API for both flex and included unlocks.
  const canExport = hasActiveSubscription || !!trackDetail?.unlockedAt;

  const {
    isRendering,
    progress: exportProgress,
    videoUrl: exportedVideoUrl,
    errorMessage: exportError,
    existingVideoUrl,
    existingAspectRatio,
    start: startExport,
  } = useLyricsVideoExport({
    trackId: trackDetail?.trackId,
    flushScene,
    // Starting an export is gated, so querying the export state for a user who
    // cannot export is a guaranteed 403 on every page load.
    enabled: canExport,
  });

  const hasTimedLyrics = !!lyricsData?.metadata?.segments?.length;

  // A reason, not a boolean: the button explains itself rather than sitting dead.
  const exportDisabledReason = !hasSavedScene
    ? "Make a change first — export renders the saved scene."
    : !waveFormData?.duration
      ? "This track has no waveform analysis yet."
      : !hasTimedLyrics
        ? "This track has no timed lyrics yet."
        : undefined;

  // The design's crumb reads "Lyrics Video / cussing-control-master.mp4", so it
  // wants the file rather than the title.
  const fileName =
    trackDetail?.fileKey?.split("/").pop() ?? trackDetail?.title ?? undefined;

  const showFormatMismatch =
    !exportedVideoUrl &&
    !exportError &&
    !!existingVideoUrl &&
    !!existingAspectRatio &&
    existingAspectRatio !== scene.canvas.aspectRatio;

  // Rendering nothing here was a blank white page for every failure — a track
  // that belongs to someone else, an unfinished upload, a slow request. "The page
  // doesn't open" with no clue why is the worst version of each of those.
  if (isTrackLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-page">
        <span className="text-sm text-gray-ink/70">Loading track…</span>
      </div>
    );
  }

  if (isTrackError || !trackDetail) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-page px-8 text-center">
        <h1 className="text-lg font-semibold text-blue-900">
          We couldn&apos;t open this track
        </h1>
        <p className="max-w-md text-sm text-gray-ink/70">
          It may have been deleted, or it belongs to a different account. Check
          you are signed in as its owner.
        </p>
        <button
          type="button"
          onClick={() => void navigate({ to: "/audiolab" })}
          className="mt-2 flex h-9 items-center rounded-full bg-blue-600 px-5 text-sm font-semibold text-white hover:bg-blue-500"
        >
          Back to Audiolab
        </button>
      </div>
    );
  }

  if (!trackDetail.fileUrl) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-page px-8 text-center">
        <h1 className="text-lg font-semibold text-blue-900">
          This track has no audio yet
        </h1>
        <p className="max-w-md text-sm text-gray-ink/70">
          The lyric video editor needs the finished audio. Once processing
          completes, come back and it will open here.
        </p>
      </div>
    );
  }

  return (
    // Plain flow, no forced viewport height: the editor renders *under* the
    // global nav (and sometimes a policy banner), so pinning its own root to
    // 100dvh would push the bottom of the player off-screen by exactly the
    // height of that chrome. The player region measures the space actually left
    // below it instead — see useEditorViewport.
    <div className="flex flex-col bg-page">
      <EditorHeader
        trackTitle={trackDetail.title ?? undefined}
        fileName={fileName}
        saveStatus={saveStatus}
        lastSavedAt={lastSavedAt}
        onBack={() =>
          void navigate({
            to: "/audiolab/tracks/$TRACK_ID",
            params: { TRACK_ID: trackId! },
          })
        }
        onExport={() => void startExport()}
        exportDisabledReason={exportDisabledReason}
        isRendering={isRendering}
        exportProgress={exportProgress}
        existingVideoUrl={existingVideoUrl}
        canExport={canExport}
        onUnlock={() => setIsUnlockOpen(true)}
        isPanelOpen={isPanelOpen}
        onTogglePanel={togglePanel}
        panelId={EDITOR_PANEL_ID}
      />

      {isBannerVisible && (
        <div className="flex-shrink-0 px-8 pb-2">
          <FirstRunBanner onDismiss={dismissBanner} />
        </div>
      )}

      {(exportedVideoUrl || exportError || showFormatMismatch) && (
        <div className="flex-shrink-0 space-y-2 px-8 pb-2">
          {exportedVideoUrl && (
            <div
              className="flex flex-wrap items-center gap-3 rounded-lg border border-green-300 bg-green-100 px-4 py-3 text-sm"
              data-testid="export-ready"
            >
              <span className="font-semibold text-green-800">
                Your video is ready.
              </span>
              {/* download, not target=_blank: this is a presigned S3 URL, and
                  opening an mp4 in a tab makes the user right-click to get the
                  file they just waited minutes for. */}
              <a
                href={exportedVideoUrl}
                download
                className="font-semibold text-green-800 underline"
              >
                Download MP4
              </a>
            </div>
          )}

          {exportError && (
            <div
              className="rounded-lg border border-red-300 bg-red-100 px-4 py-3 text-sm text-red-700"
              data-testid="export-error"
            >
              {exportError}
            </div>
          )}

          {/* An existing export at a different aspect ratio is not the video on
              screen. Offering it to download without saying so would be worse
              than not offering it. */}
          {showFormatMismatch && (
            <div className="rounded-lg border border-yellow-300 bg-yellow-100 px-4 py-3 text-sm text-blue-900">
              The video you can download was rendered at {existingAspectRatio}.
              Generate a new one to get {scene.canvas.aspectRatio}.
            </div>
          )}
        </div>
      )}

      {/* The player alone in the flow, at a measured pixel height so the card's
          own flex chain and the frame's fit-to-box have a definite box to work
          from. */}
      <div
        ref={viewportRef}
        style={{ height: viewportHeight }}
        className="px-8 pb-4"
      >
        <PlayerCard
          className="h-full"
          canvasWidth={scene.canvas.width}
          canvasHeight={scene.canvas.height}
          // The panel never reflows the card — it stays out of layout flow.
          // This only tells the frame how much of the card is currently hidden,
          // so it fits itself into what you can actually see. The panel's
          // widest size, not its current one: narrowing it to a viewport that
          // cannot fit it is a runtime measurement, and PlayerCard is where
          // those happen — read here it would be a render-time `innerWidth`
          // that no resize invalidates.
          reservedRight={isPanelOpen ? EDITOR_PANEL_MAX_WIDTH : 0}
          headerActions={
            <>
              <span className="text-sm font-semibold text-gray-ink/70">
                Format
              </span>
              <SegmentedTabs
                ariaLabel="Video format"
                size="sm"
                options={FORMAT_TABS}
                value={scene.canvas.aspectRatio}
                onChange={changeFormat}
              />
            </>
          }
          footer={
            <PlaybackBar
              isPlaying={isPlaying}
              currentTime={currentTime}
              duration={duration}
              waveFormData={waveFormData}
              onTogglePlay={togglePlay}
              onSeek={seek}
              actions={
                <button
                  type="button"
                  onClick={() =>
                    document.getElementById("lyrics-video")?.requestFullscreen()
                  }
                  aria-label="Full screen"
                  className="flex h-9 w-9 items-center justify-center rounded-full text-gray-ink/70 hover:bg-gray-50"
                >
                  <FiMaximize className="h-5 w-5" />
                </button>
              }
            />
          }
        >
          <LyricsVideoStage
            scene={scene}
            currentTime={currentTime}
            waveFormData={waveFormData}
            lyricsData={lyricsData}
          />
        </PlayerCard>

        <audio ref={audioRef} className="hidden" />
      </div>

      <EditorPanel
        id={EDITOR_PANEL_ID}
        isOpen={isPanelOpen}
        onClose={closePanel}
        // Starts where the player starts, not at the top of the window. Pinned
        // to top-0 it sat over the editor header and covered Export — and the
        // very toggle that closes it.
        top={viewportTop}
        height={viewportHeight}
        labelledBy={panelTabId(EDITOR_PANEL_TAB_PREFIX, activeTab)}
        tabs={
          <PanelTabs
            ariaLabel="Editor controls"
            idPrefix={EDITOR_PANEL_TAB_PREFIX}
            options={PANEL_TABS}
            value={activeTab}
            onChange={setActiveTab}
          />
        }
        headerActions={
          <button
            type="button"
            onClick={undo}
            disabled={!previousScene}
            data-testid="undo-scene"
            className="flex h-11 items-center gap-1 rounded-full px-3 text-sm font-bold text-blue-900 hover:bg-gray-50 disabled:opacity-40 disabled:hover:bg-transparent"
          >
            <FiRotateCcw className="h-4 w-4" />
            Undo
          </button>
        }
      >
        {activeTab === "styles" && (
          <PresetPicker
            activePresetId={activePreset?.id}
            onSelect={selectPreset}
            onRandomize={randomizeStyle}
          />
        )}

        {activeTab === "text" && (
          <>
            <PanelSection>
              <LyricsPanel value={scene.lyrics} onChange={updateLyrics} />
            </PanelSection>
            <AdvancedLyricsPanel
              scene={scene}
              onChange={updateLyrics}
              onShadowChange={updateShadow}
            />
          </>
        )}

        {activeTab === "background" && (
          <PanelSection>
            <BackgroundPanel
              value={scene.background}
              onChange={updateBackground}
              coverArtUrl={trackDetail.imageUrl}
              trackTitle={trackDetail.title ?? undefined}
            />
          </PanelSection>
        )}

        {/* Each visualizer is its own collapsible block, with the type selector
            on the section's title row. */}
        {activeTab === "animations" && (
          <VisualizerPanel
            visualizers={scene.visualizers}
            inheritedColor={scene.lyrics.color}
            onAdd={addVisualizer}
            onUpdate={updateVisualizer}
            onRemove={removeVisualizer}
          />
        )}

        {/* Autosave covers this, but the design's "Changes Saved" badge is
            passive — an explicit save stays reachable for anyone who wants to
            be sure before leaving. */}
        <div className="flex justify-end pt-2">
          <button
            type="button"
            onClick={() => void saveScene()}
            className="text-xs font-semibold text-gray-ink/70 underline underline-offset-2 hover:text-blue-900"
          >
            Save now
          </button>
        </div>
      </EditorPanel>

      {isUnlockOpen && (
        <UnlockAssetsModal
          trackId={trackDetail.trackId}
          onClose={() => setIsUnlockOpen(false)}
        />
      )}
    </div>
  );
}
